import '../../Shared/js/polyfills';

import { onArrayPush } from '../../Shared/js/utils/array';
import { pushDataLayerItemWidget } from '../../Shared/js/utils/datalayer';
import * as EventHelper from './common/utils/EventHelper';
import * as ViewportHelper from './common/utils/ViewportHelper';

const createController = function () {
    return {
        getContainer: function () {
            let container;

            if (this.args.containerId === '') {
                container = this.findScript();
            } else {
                container = document.getElementById(this.args.containerId);
            }

            if (!container) {
                throw new Error('Container not found!');
            }

            return container;
        },

        uuid: function () {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = (Math.random() * 16) | 0,
                    v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },

        findScript: function () {
            const url = this.args.scriptUrl.replace(/&amp;/g, '&');
            const urlParts = url.split('?');
            const paramsMatch = this.paramsMatch.bind(this);

            for (let i = 0; i < document.scripts.length; i++) {
                const src = document.scripts[i].src.replace(/&amp;/g, '&');
                const srcParts = src.split('?');

                if (urlParts.length === srcParts.length && urlParts[0] === srcParts[0]) {
                    if (urlParts.length === 1) {
                        return document.scripts[i];
                    }
                    if (paramsMatch(urlParts[1].split('&'), srcParts[1].split('&'))) {
                        return document.scripts[i];
                    }
                }
            }
            return null;
        },

        // TODO: Use Lodash
        paramsMatch: function (urlParams: unknown[], srcParams: unknown[]) {
            if (urlParams.length !== srcParams.length) return false;
            const inArray = this.inArray;
            for (let x = 0; x < urlParams.length; x++) {
                if (!inArray(urlParams[x], srcParams)) {
                    return false;
                }
            }
            return true;
        },

        // TODO: Use Lodash
        inArray: function (value: string, array: string[]) {
            for (let x = 0; x < array.length; x++) {
                if (value.toLowerCase() === array[x].toLowerCase()) {
                    return true;
                }
            }
            return false;
        },

        appendQuery: function (url: string, query: string) {
            if (url.indexOf('?') === -1) {
                return url + '?' + query;
            }
            return url + '&' + query;
        },

        addDataAttribute: function (frame: HTMLIFrameElement, name: string) {
            const container = this.getContainer();
            const value = container.getAttribute('data-' + name);
            if (value) {
                frame.src = this.appendQuery(frame.src, name + '=' + encodeURIComponent(value));
            }
        },

        onLoad: function () {
            const container = this.getContainer();
            if (!container) {
                return;
            }

            const parent = this.getParentContainer(container);
            if (ViewportHelper.isInViewport(parent)) {
                this.loadWidget();
                return;
            }

            this.onScrollHandler = this.onScroll.bind(this);

            const options = <IntersectionObserverInit>{
                root: null, // viewport
                threshold: 0.1
            };

            this.observer = new IntersectionObserver(this.onScrollHandler, options);
            this.observer.observe(parent);
        },

        getParentContainer(el: HTMLElement) {
            const parent = el.parentElement;
            if (!parent) {
                return el;
            }

            if (parent.clientHeight === 0) {
                return this.getParentContainer(parent);
            }

            return parent;
        },

        onScroll: function (entries: IntersectionObserverEntry[]) {
            if (this.loadTriggered) {
                return;
            }

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                if (entry.isIntersecting) {
                    this.observer.disconnect();
                    this.loadWidget();
                    return;
                }
            }
        },

        loadWidget: function () {
            this.loadTriggered = true;

            const container = this.getContainer();

            const startTime = Date.now();
            pushDataLayerItemWidget({
                event: 'widget_preload_begin',
                widget_placement: 'iframe'
            });

            const location = this.getLocation(container);

            console.debug({
                'wo->_loadWidget': this.args.frameSource,
                location: location
            });

            const frame = document.createElement('iframe');
            frame.id = this.uid;

            const referrer = `_r=${encodeURIComponent(location)}`;
            frame.src = this.appendQuery(this.args.frameSource, referrer);

            frame.setAttribute('width', '100%');
            frame.setAttribute('scrolling', 'no');
            frame.setAttribute('frameborder', '0');
            frame.setAttribute('seamless', 'seamless');
            frame.setAttribute('allowtransparency', 'true');
            frame.style.height = this.args.isCta ? '34px' : '260px';

            if (this.args.allowFullscreen) {
                frame.setAttribute('allowfullscreen', '');
            }

            this.addDataAttribute(frame, 'query');

            const parentNode = container.parentNode;
            parentNode.replaceChild(frame, container);

            frame.onload = () => {
                const onMessage = (event: Event) => {
                    this.resize(frame, event);
                };

                EventHelper.addEventListener([window], 'message', onMessage);

                const endTime = Date.now();
                pushDataLayerItemWidget({
                    event: 'widget_preload_end',
                    widget_placement: 'iframe',
                    duration: (endTime - startTime) / 1000
                });
            };
        },

        getLocation(el: HTMLElement) {
            const defaultLocation = window.top.location.href;

            // TODO: Support list of attributes for different website implementations
            const key = 'data-article-permalink';
            const article = el.closest(`[${key}]`);
            if (!article) {
                return defaultLocation;
            }

            const permalink = article.getAttribute(key);
            return permalink || defaultLocation;
        },

        resize: function (frame: HTMLIFrameElement, event: Event) {
            const callback = () => this._resize(frame, event);
            requestIdleCallback(callback, { timeout: 500 });
        },

        _resize: function (frame: HTMLIFrameElement, event: Event & { data: ResizeEventArgs; source: unknown }) {
            const newSize = `${event.data.height}px`;

            if (
                event.data &&
                event.data.height &&
                Object.prototype.hasOwnProperty.call(event.data, 'sentinel') &&
                event.data.sentinel === 'whistleout-widget' &&
                event.data.url === frame.src &&
                event.source === frame.contentWindow &&
                frame.style.height !== newSize
            ) {
                console.debug(`Resizing widget: ${frame.style.height}->${newSize}`, new URL(event.data.url));
                frame.style.height = newSize;
            }
        },

        init: function (args: ApiParams) {
            this.args = args;
            this.uid = this.uuid();

            this.loadTriggered = false;
            this.loadWidgetTaskId = null;
            this.resizeTaskId = null;

            pushDataLayerItemWidget({
                event: 'widget_preload_init',
                widget_placement: 'iframe'
            });

            this.onLoad();
        }
    };
};

const supportedAPI = ['show'];

function apiHandler(api: string, params: ApiParams) {
    if (!api) {
        throw Error('API method required');
    }

    api = api.toLowerCase();
    if (supportedAPI.indexOf(api) === -1) {
        throw Error(`Method ${api} is not supported`);
    }

    console.debug(`Handling API call ${api}`, params);

    const controller = createController();
    switch (api) {
        case 'show':
            controller.init(params);
            break;
        default:
            console.warn(`No handler defined for ${api}`);
    }
}

// Based on: https://blog.jenyay.com/building-javascript-widget/
function processQueue(queue: ApiQueueItem[]) {
    console.debug('Processing widget preload queue...');

    if (!queue) {
        console.debug('Processing widget preload queue: empty queue. SKIP');
        return;
    }

    for (let i = 0; i < queue.length; i++) {
        const queueItem = queue[i];
        if (queueItem.isProcessed) {
            continue;
        }

        console.debug(`Processing widget preload queue, item ${i}...: ${queueItem[0]}`, queueItem);
        apiHandler(queueItem[0], queueItem[1]);

        // TODO: Consider removing processed items for better performance
        queueItem.isProcessed = true;

        console.debug(`Processing widget preload queue, item ${i}... DONE`);
    }

    console.debug('Processing widget preload queue... DONE');
}

function main() {
    const queue = window['wo-widget'].pq;
    onArrayPush(queue, added => {
        processQueue(added);
    });

    processQueue(queue);
}

main();
